import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CButton,
  CFormInput,
  CFormSelect,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPencil, cilPlus, cilSave, cilX, cilTrash } from "@coreui/icons";
import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";

const GENDER_OPTIONS = [
  { value: "", label: "" },
  { value: "Masculino", label: "Masculino" },
  { value: "Feminino", label: "Feminino" },
];

const monthToApi = (m) => (m ? `${m}-01` : null);
const apiToMonth = (d) => (d ? d.slice(0, 7) : "");

const Employees = ({
  clientId,
  managementDailyJourney: initialManagementDailyJourney = 8,
  onManagementDailyJourneySaved = () => {},
  onClose,
  setToast,
}) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [managementDailyJourney, setManagementDailyJourney] = useState(
    initialManagementDailyJourney,
  );
  const [updatingManagementJourney, setUpdatingManagementJourney] =
    useState(false);

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const tableContainerRef = useRef(null);
  const activeInputRef = useRef(null);
  const newRowRef = useRef(null);

  useEffect(() => {
    apiClient
      .get(`/projects/management/${clientId}/employees/`)
      .then((response) => {
        const rows = response.data.employees.map((e) => ({
          ...e,
          exit_date: apiToMonth(e.exit_date),
        }));
        setEmployees(rows);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    setManagementDailyJourney(initialManagementDailyJourney);
  }, [initialManagementDailyJourney]);

  const dataWithAddRow = useMemo(() => {
    if (editingId !== "__new__") return employees;
    return [
      ...employees,
      {
        id: "__new__",
        name: draft?.name ?? "",
        gender: draft?.gender ?? "",
        exit_date: draft?.exit_date ?? "",
        _isNew: true,
      },
    ];
  }, [employees, editingId, draft]);

  useEffect(() => {
    if (!editingId) return;
    const t = setTimeout(() => {
      activeInputRef.current?.focus();
      newRowRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 50);
    return () => clearTimeout(t);
  }, [editingId]);

  const goToLastPageIfPossible = () => {
    const pageButtons = document.querySelectorAll(
      "button#pagination-last-page",
    );

    if (!pageButtons.length) return;

    const lastPageButton = pageButtons[pageButtons.length - 1];
    if (lastPageButton && !lastPageButton.disabled) {
      lastPageButton.click();
    } else if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({
        top: tableContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const beginEdit = (row) => {
    setEditingId(row.id);
    setDraft({
      id: row.id,
      name: row.name,
      gender: row.gender ?? "",
      exit_date: row.exit_date ?? "",
    });
  };

  const beginAdd = () => {
    setEditingId("__new__");
    setDraft({ name: "", gender: "", exit_date: "" });
    goToLastPageIfPossible();
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(null);
  };

  const onChangeField = (field, value) =>
    setDraft((d) => ({ ...d, [field]: value }));

  const saveExisting = async () => {
    try {
      const payload = { ...draft, exit_date: monthToApi(draft.exit_date) };
      const { data: updated } = await apiClient.put(
        `/projects/management/${clientId}/employee/${draft.id}/`,
        payload,
      );
      setEmployees((prev) =>
        prev.map((e) =>
          e.id === draft.id
            ? { ...updated, exit_date: apiToMonth(updated.exit_date) }
            : e,
        ),
      );
      setToast({
        title: "Funcionário",
        message: "Funcionário alterado com sucesso!",
        type: "success",
      });
      cancel();
    } catch (e) {
      setToast({
        title: "Funcionário",
        message: "Ocorreu um erro a atualizar o funcionário",
        type: "error",
      });
    }
  };

  const createNew = async () => {
    try {
      const payload = {
        name: draft.name,
        gender: draft.gender,
        exit_date: monthToApi(draft.exit_date),
      };
      const { data: created } = await apiClient.post(
        `/projects/management/${clientId}/employees/`,
        payload,
      );
      setEmployees((prev) => [
        ...prev,
        { ...created, exit_date: apiToMonth(created.exit_date) },
      ]);
      setToast({
        title: "Novo Funcionário",
        message: "Funcionário criado com sucesso!",
        type: "success",
      });
      cancel();
      setTimeout(() => {
        goToLastPageIfPossible();
        document
          .getElementById(`row-${created.id}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    } catch (e) {
      setToast({
        title: "Novo Funcionário",
        message: "Ocorreu um erro a criar o funcionário!",
        type: "error",
      });
    }
  };

  const requestDeleteEmployee = (id) => setConfirmDeleteId(id);

  const saveManagementDailyJourney = async () => {
    const nextValue = Number(managementDailyJourney);
    if (!Number.isInteger(nextValue) || nextValue <= 0) {
      setToast({
        title: "Jornada diária",
        message: "Indica uma jornada diária válida, em horas inteiras.",
        type: "error",
      });
      return;
    }

    try {
      setUpdatingManagementJourney(true);
      const response = await apiClient.put(
        `/projects/management/${clientId}/settings/`,
        {
          management_daily_journey: nextValue,
        },
      );
      const savedValue =
        Number(response?.data?.management_daily_journey) || nextValue;
      setManagementDailyJourney(savedValue);
      onManagementDailyJourneySaved(savedValue);
      setToast({
        title: "Jornada diária",
        message: `Jornada diária da empresa atualizada para ${savedValue}h.`,
        type: "success",
      });
    } catch (e) {
      setToast({
        title: "Jornada diária",
        message: "Não foi possível atualizar a jornada diária da empresa.",
        type: "error",
      });
    } finally {
      setUpdatingManagementJourney(false);
    }
  };

  const confirmDelete = async () => {
    const id = confirmDeleteId;
    if (!id) return;
    try {
      await apiClient.delete(
        `/projects/management/${clientId}/employee/${id}/`,
      );
      setEmployees((prev) => prev.filter((e) => e.id !== id));
      setToast({
        title: "Funcionário",
        message: "Funcionário eliminado com sucesso!",
        type: "success",
      });
    } catch (e) {
      setToast({
        title: "Funcionário",
        message: "Ocorreu um erro a eliminar o funcionário",
        type: "error",
      });
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const isRowActive = (row) =>
    editingId &&
    (row.id === editingId || (editingId === "__new__" && row.id === "__new__"));
  const isAdding = editingId === "__new__";

  const columns = [
    {
      name: "Nome",
      selector: (row) => row.name,
      sortable: true,
      width: "45%",
      cell: (row) => {
        const active = isRowActive(row);
        if (active) {
          return (
            <CFormInput
              ref={activeInputRef}
              size="sm"
              value={draft.name}
              onChange={(e) => onChangeField("name", e.target.value)}
              placeholder="Nome"
            />
          );
        }
        return (
          <div
            id={`row-${row.id}`}
            ref={row._isNew ? newRowRef : undefined}
            className="text-truncate fw-semibold"
          >
            {row.name}
          </div>
        );
      },
      style: {
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
      },
    },
    {
      name: "Data de Saída",
      selector: (row) => row.exit_date || "-",
      sortable: true,
      center: true,
      width: "20%",
      cell: (row) => {
        const active = isRowActive(row);
        if (active) {
          return (
            <CFormInput
              size="sm"
              type="month"
              value={draft.exit_date || ""}
              onChange={(e) => onChangeField("exit_date", e.target.value)}
            />
          );
        }
        return row.exit_date || "-";
      },
    },
    {
      name: "Género",
      selector: (row) => row.gender,
      sortable: true,
      center: true,
      width: "15%",
      cell: (row) => {
        const active = isRowActive(row);
        if (active) {
          return (
            <CFormSelect
              size="sm"
              value={draft.gender || ""}
              onChange={(e) => onChangeField("gender", e.target.value)}
              options={GENDER_OPTIONS}
            />
          );
        }
        return row.gender || "";
      },
    },
    {
      name: "Ações",
      width: "20%",
      center: true,
      cell: (row) => {
        const active = isRowActive(row);
        if (active) {
          return (
            <div className="d-flex gap-1">
              <CButton
                color="success"
                size="sm"
                className="rounded-1 px-2 py-1"
                onClick={isAdding ? createNew : saveExisting}
                title={isAdding ? "Criar" : "Guardar"}
              >
                <CIcon icon={cilSave} />
              </CButton>
              <CButton
                color="secondary"
                size="sm"
                className="rounded-1 px-2 py-1"
                onClick={cancel}
                title="Cancelar"
              >
                <CIcon icon={cilX} />
              </CButton>
            </div>
          );
        }
        return (
          <div className="d-flex gap-1">
            <CButton
              color="info"
              size="sm"
              className="rounded-1 px-2 py-1"
              disabled={!!editingId}
              onClick={() => beginEdit(row)}
              title="Editar"
            >
              <CIcon icon={cilPencil} />
            </CButton>
            <CButton
              color="danger"
              size="sm"
              className="rounded-1 px-2 py-1"
              disabled={!!editingId}
              onClick={() => requestDeleteEmployee(row.id)}
              title="Eliminar"
            >
              <CIcon icon={cilTrash} />
            </CButton>
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <CModal onClose={onClose} size="lg" backdrop="static" scrollable visible>
      <CModalHeader>
        <CModalTitle>Gestão de Funcionários</CModalTitle>
      </CModalHeader>
      <CModalBody ref={tableContainerRef}>
        <div className="border rounded p-3 mb-3 bg-light">
          <div className="d-flex flex-column flex-md-row align-items-md-end gap-2">
            <div style={{ maxWidth: 220, width: "100%" }}>
              <label className="form-label mb-1">
                Jornadas Diária da Empresa
              </label>
              <CFormInput
                type="number"
                min={1}
                step={1}
                value={managementDailyJourney ?? ""}
                onChange={(e) => setManagementDailyJourney(e.target.value)}
              />
            </div>
            <CButton
              color="primary"
              onClick={saveManagementDailyJourney}
              disabled={updatingManagementJourney}
            >
              {updatingManagementJourney ? (
                <CSpinner component="span" size="sm" className="me-2" />
              ) : (
                <CIcon icon={cilSave} className="me-2" />
              )}
              Guardar Jornada
            </CButton>
          </div>
        </div>

        <div className="d-flex flex-row-reverse pb-2">
          <CButton
            color="success"
            className="d-flex align-items-center rounded-1 gap-1 px-2 py-1"
            disabled={!!editingId}
            onClick={beginAdd}
            title="Adicionar"
          >
            <CIcon icon={cilPlus} />
            <small>Adicionar</small>
          </CButton>
        </div>

        <DataTableComponent
          columns={columns}
          data={dataWithAddRow}
          loading={loading}
          error={error}
        />

        <CModal
          visible={!!confirmDeleteId}
          backdrop="static"
          onClose={() => setConfirmDeleteId(null)}
        >
          <CModalHeader>
            <CModalTitle>Eliminar funcionário</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <p className="mb-2 fw-semibold text-danger">
              Esta ação é irreversível.
            </p>
            <p>
              Tem a certeza de que pretende eliminar este funcionário? Não
              poderá desfazer esta operação.
            </p>
            <div className="d-flex gap-2 justify-content-end mt-3">
              <CButton
                color="secondary"
                size="sm"
                className="rounded-1"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancelar
              </CButton>
              <CButton
                color="danger"
                size="sm"
                className="rounded-1"
                onClick={confirmDelete}
              >
                Eliminar definitivamente
              </CButton>
            </div>
          </CModalBody>
        </CModal>
      </CModalBody>
    </CModal>
  );
};

export default Employees;
